import { HazardPlusTotal } from "@/features/pe_aldd";
import { PeaSummaryResponse, PeaSummaryRow } from "@/store/services/facades/types/pea";
import { Rcp } from "@/types/rcp";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCollectionState } from "../context/CollectionContext";
import { useMetricOptions } from "../hooks/useMetricOptions";
import { PointDetailsPermissions } from "../types";
import SelectMetricOptions from "./SelectMetricOptions";
import { ChartHeader, Header } from "./charts/common/Text";
import { formatMetricValue, formatYAxisTick, getMetricShortLabel, isHazardMetricDataAvailable, MetricKey, multiplierForMetric, PERCENT_METRICS } from "./charts/common/utils";
import { ADAPTATION_TERM_COLORS, adaptationConfig, adaptationTypeValues, type AdaptationType } from "@/types/adaptation";
import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import { LineChart, type DataSeries } from "@/components/charts/LineChart";
import CollectionFiltersLabel from "./CollectionFiltersLabel";
type PortfolioRow = PeaSummaryRow & { [K in MetricKey]: Record<string, number> };
const filterRowsByHazard = (data: PeaSummaryResponse, hazard: HazardPlusTotal): PeaSummaryResponse => {
  return data.filter(row => {
    const rowHazard = (row as PortfolioRow).hazard;
    return hazard === "total" ? String(rowHazard) === "total" : String(rowHazard) === String(hazard);
  });
};

// build the per-year risk metric values for a pathway
const buildPathwayPoints = (rows: PeaSummaryResponse, metric: MetricKey, rcp: Rcp): {
  year: number;
  value: number;
}[] => {
  const row = rows.find(r => r.climate_scenario as Rcp === rcp);
  if (!row) return [];
  const fieldData = (row as PortfolioRow)[metric];
  if (!fieldData) return [];
  const multiplier = multiplierForMetric(metric);
  return Object.entries(fieldData).map(([year, value]) => ({
    year: Number(year),
    value: value * multiplier
  })).sort((a, b) => a.year - b.year);
};
type AdaptationRiskSeries = {
  id: string;
  adaptationType: AdaptationType;
  data: PeaSummaryResponse;
};
interface AdaptationPhysicalRiskProps {
  id: string;
  dataList: AdaptationRiskSeries[];
  highlightedId?: string | null;
  onHighlightChange?: (id: string | null) => void;
  translationPrefix?: string;
  permissions?: PointDetailsPermissions;
  tag?: React.ReactNode;
}
export const AdaptationPhysicalRisk = ({
  id,
  dataList,
  highlightedId,
  onHighlightChange,
  translationPrefix = "portfolios.portfolioLevel.adaptation.assetRisk",
  permissions,
  tag
}: AdaptationPhysicalRiskProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: translationPrefix
  });
  const {
    hazard,
    rcp
  } = useCollectionState();
  const [metric, setMetric] = useState<MetricKey>("avg_damage_mtd");
  const isPointLevel = translationPrefix.includes("pointLevel");
  const {
    metricOptions,
    selectedMetric
  } = useMetricOptions({
    metric,
    setMetric,
    showTtip: true,
    isPointLevel,
    permissions,
    translationPrefix
  });
  const hazardDataAvailable = isHazardMetricDataAvailable(hazard, metric);
  // Same per-metric copy the other asset risk charts use, e.g. why MVAR is not calculated for extreme heat.
  const hazardDataUnavailableMessage = t(`charts.${getMetricShortLabel(metric).toLowerCase()}.unavailableMessage`, "No Data Available");
  const pathways: PathwaySeries[] = useMemo(() => {
    if (!hazardDataAvailable) return [];
    const built = dataList.map(entry => ({
      id: entry.id,
      adaptationType: entry.adaptationType,
      points: buildPathwayPoints(filterRowsByHazard(entry.data, hazard), metric, rcp)
    }));

    // auto adaptated pathway diverges from baseline from the current year. past data will be same as baseline
    const currentYear = new Date().getFullYear();
    const baseline = built.find(p => p.adaptationType === "baseline");
    if (!baseline) return built;
    const baselineByYear = new Map(baseline.points.map(point => [point.year, point.value]));
    return built.map(pathway => pathway.adaptationType !== "enhanced_resilience" ? pathway : {
      ...pathway,
      points: pathway.points.map(point => point.year <= currentYear && baselineByYear.has(point.year) ? {
        ...point,
        value: baselineByYear.get(point.year)!
      } : point)
    });
  }, [dataList, hazard, metric, rcp, hazardDataAvailable]);
  const yAxisUnit = PERCENT_METRICS.includes(metric) ? "%" : "$";
  if (!metricOptions.length || !selectedMetric) return null;
  return <div id={id} className="grid grid-cols-12 @container/wrapper" data-sentry-component="AdaptationPhysicalRisk" data-sentry-source-file="AdaptationPhysicalRisk.tsx">
      <div className="col-span-12 mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Header title={t("title")} data-sentry-element="Header" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />
          {tag}
        </div>
        <ContextualInfoCollapse data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="AdaptationPhysicalRisk.tsx">{t("description")}</ContextualInfoCollapse>
      </div>

      <div className="col-span-12 mb-0">
        <SelectMetricOptions variant="tabs" metric={metric} setMetric={setMetric} metricOptions={metricOptions} data-sentry-element="SelectMetricOptions" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />
      </div>
      <div className="col-span-12 grid grid-cols-12 gap-2 rounded-2xl rounded-tl-none border border-gray-200 bg-surface p-2 pt-4 shadow-sm @2xl/wrapper:p-4">
        <div className="col-span-12 text-right @4xl/wrapper:col-span-4">
          <CollectionFiltersLabel withIcons data-sentry-element="CollectionFiltersLabel" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />
        </div>
        <div className="col-span-12 mb-2 @4xl/wrapper:order-first @4xl/wrapper:col-span-8">
          <ChartHeader title={selectedMetric.title} description={selectedMetric.description} contextualInfoButtonId="metrics" data-sentry-element="ChartHeader" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />
        </div>

        <div className="col-span-12 w-full rounded-xl border border-gray-100 px-2 py-4 @2xl/wrapper:p-4">
          <div className="h-[480px]">
            <AdaptationPhysicalRiskLineChart metric={selectedMetric.value} pathways={pathways} yAxisUnit={yAxisUnit} metricLabel={selectedMetric.shortLabel} highlightedId={highlightedId} onHighlightChange={onHighlightChange} unavailableMessage={hazardDataAvailable ? undefined : hazardDataUnavailableMessage} data-sentry-element="AdaptationPhysicalRiskLineChart" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />
          </div>
        </div>
      </div>
    </div>;
};
export type PathwaySeries = {
  id: string;
  adaptationType: AdaptationType;
  points: {
    year: number;
    value: number;
  }[];
};
const PATHWAY_LINE_COLORS: Partial<Record<AdaptationType, string>> = {
  low_resilience: `${ADAPTATION_TERM_COLORS["low_resilience"]}${99}`,
  modern_resilience: `${ADAPTATION_TERM_COLORS["modern_resilience"]}${99}`
};

// baseline and the intervention pathway anchor the chart; the two bounds recede behind them
const ANCHOR_STROKE_WIDTH = 3.5;
const BOUND_STROKE_WIDTH = 1.25;
const SELECTED_STROKE_WIDTH = 5;
const AUTO_ADAPTED_DASH = "8 5";
const PATHWAY_STROKE_WIDTHS: Partial<Record<AdaptationType, number>> = {
  baseline: ANCHOR_STROKE_WIDTH,
  enhanced_resilience: ANCHOR_STROKE_WIDTH,
  low_resilience: BOUND_STROKE_WIDTH,
  modern_resilience: BOUND_STROKE_WIDTH
};

// the show adapted line on top, its dashed so overlapping line shows through
const PATHWAY_DRAW_ORDER: Partial<Record<AdaptationType, number>> = {
  low_resilience: 0,
  modern_resilience: 0,
  baseline: 2,
  enhanced_resilience: 3
};
const drawOrderFor = (adaptationType: AdaptationType) => PATHWAY_DRAW_ORDER[adaptationType] ?? 1;

/** Legend follows the pathway order, not the draw order; series that are not a pathway trail. */
const legendOrderFor = (key: string, pathways: PathwaySeries[]) => {
  const adaptationType = pathways.find(pathway => pathway.id === key)?.adaptationType;
  return adaptationType ? adaptationTypeValues.indexOf(adaptationType) : adaptationTypeValues.length;
};
const AdaptationPhysicalRiskLegend = ({
  series,
  highlightedId,
  emphasizedKeys = [],
  onHighlightChange
}: {
  series: DataSeries[];
  highlightedId?: string | null;
  emphasizedKeys?: string[];
  onHighlightChange?: (id: string | null) => void;
}) => {
  const hasSelection = emphasizedKeys.length > 0;
  return <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 pb-1 pr-14 text-xs @2xl/wrapper:text-sm" data-sentry-component="AdaptationPhysicalRiskLegend" data-sentry-source-file="AdaptationPhysicalRisk.tsx">
      {series.map(item => {
      if (item.band) {
        const fromColor = item.band.fromColor ?? item.color;
        const toColor = item.band.toColor ?? item.color;
        return <div key={item.key} className="flex items-center gap-1">
              <div className="h-3 w-3 rounded-sm" style={{
            background: `linear-gradient(to bottom, ${fromColor}, ${toColor})`
          }} />
              <span className="tracking-wide">{item.name ?? item.key}</span>
              {item.contextId && <ContextualInfoButton id={item.contextId} />}
            </div>;
      }
      const isEmphasized = emphasizedKeys.includes(item.key);
      const isSelected = highlightedId === item.key;
      return <div key={item.key} className="flex items-center gap-1">
            <button type="button" onClick={() => onHighlightChange?.(isSelected ? null : item.key)} className={`flex items-center gap-1 transition-opacity ${hasSelection && !isEmphasized ? "opacity-40 hover:opacity-100" : "opacity-100"}`}>
              <div className="h-3 w-3 rounded-sm" style={{
            backgroundColor: item.color
          }} />
              <span className={`tracking-wide ${isEmphasized ? "font-semibold" : ""}`}>{item.name ?? item.key}</span>
            </button>
            {item.contextId && <ContextualInfoButton rootClassName="text-xs" id={item.contextId} />}
          </div>;
    })}
    </div>;
};
export const AdaptationPhysicalRiskLineChart = ({
  metric,
  pathways,
  yAxisUnit,
  metricLabel,
  highlightedId,
  onHighlightChange,
  unavailableMessage
}: {
  metric: MetricKey;
  pathways: PathwaySeries[];
  yAxisUnit: "%" | "$";
  metricLabel?: string;
  highlightedId?: string | null;
  onHighlightChange?: (id: string | null) => void;
  /** Shown in place of the chart when the selected hazard has no data for the selected metric. */
  unavailableMessage?: string;
}) => {
  const {
    year,
    setYear
  } = useCollectionState();
  const {
    t
  } = useTranslation("terms");
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.hazardBreakdown"
  });

  // Merge each pathway's points into rows keyed by year, one property per pathway id.
  const yearSet = new Set<number>();
  pathways.forEach(pathway => pathway.points.forEach(point => yearSet.add(point.year)));
  const years = Array.from(yearSet).sort((a, b) => a - b);
  const valueByPathwayYear = new Map<string, Map<number, number>>();
  pathways.forEach(pathway => {
    const yearMap = new Map<number, number>();
    pathway.points.forEach(point => yearMap.set(point.year, point.value));
    valueByPathwayYear.set(pathway.id, yearMap);
  });
  const chartData: Record<string, unknown>[] = years.map(y => {
    const row: Record<string, unknown> = {
      year: y
    };
    pathways.forEach(pathway => {
      row[pathway.id] = valueByPathwayYear.get(pathway.id)?.get(y);
    });
    return row;
  });
  const baselineId = pathways.find(pathway => pathway.adaptationType === "baseline")?.id;
  const emphasizedKeys = highlightedId ? Array.from(new Set([highlightedId, baselineId].filter(Boolean))) as string[] : [];
  const casingActive = emphasizedKeys.length === 2;
  const lineSeries: DataSeries[] = [...pathways].sort((a, b) => drawOrderFor(a.adaptationType) - drawOrderFor(b.adaptationType)).map(pathway => {
    const isAutoAdapted = pathway.adaptationType === "enhanced_resilience";
    return {
      key: pathway.id,
      color: PATHWAY_LINE_COLORS[pathway.adaptationType] ?? ADAPTATION_TERM_COLORS[pathway.adaptationType],
      name: t(`adaptationTypes.${pathway.adaptationType}`) as string,
      // an explicit dash wins in every state; the rest opt out of the de-emphasis dash so they
      // never start reading as the auto adapted line
      ...(isAutoAdapted ? {
        strokeDasharray: AUTO_ADAPTED_DASH
      } : {
        alwaysSolid: true
      }),
      contextId: adaptationConfig[pathway.adaptationType]?.contextId,
      baseStrokeWidth: PATHWAY_STROKE_WIDTHS[pathway.adaptationType],
      emphasisStrokeWidth: casingActive ? pathway.id === baselineId ? ANCHOR_STROKE_WIDTH : pathway.id === highlightedId ? SELECTED_STROKE_WIDTH : undefined : undefined
    };
  });

  // gradient only displayed when both low/high resilience data is available
  const lowResPathway = pathways.find(pathway => pathway.adaptationType === "low_resilience");
  const highResPathway = pathways.find(pathway => pathway.adaptationType === "modern_resilience");
  const envelopeSeries: DataSeries[] = lowResPathway && highResPathway ? [{
    key: "assetUncertainty",
    color: ADAPTATION_TERM_COLORS.low_resilience,
    name: t("assetUncertainty"),
    contextId: "assetUncertainty",
    band: {
      from: lowResPathway.id,
      to: highResPathway.id,
      fromColor: ADAPTATION_TERM_COLORS.low_resilience,
      toColor: ADAPTATION_TERM_COLORS.modern_resilience
    }
  }] : [];
  const series: DataSeries[] = [...lineSeries, ...envelopeSeries];
  return <LineChart formatTick={value => formatYAxisTick(metric, value)} chartData={chartData} series={series} formatValue={value => formatMetricValue(yAxisUnit, value, yAxisUnit === "%" ? 4 : 2)} metricLabel={metricLabel} selectedDataKeys={emphasizedKeys} selectedYear={year} onYearChange={setYear} onSeriesClick={key => onHighlightChange?.(key)} legendContent={props => <AdaptationPhysicalRiskLegend {...props} series={[...props.series].sort((a, b) => legendOrderFor(a.key, pathways) - legendOrderFor(b.key, pathways))} highlightedId={highlightedId} emphasizedKeys={emphasizedKeys} onHighlightChange={onHighlightChange} />} emptyMessage={unavailableMessage ?? tHazardBreakdown("unavailableMessage")} showArea data-sentry-element="LineChart" data-sentry-component="AdaptationPhysicalRiskLineChart" data-sentry-source-file="AdaptationPhysicalRisk.tsx" />;
};